import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, ExternalLink, ArrowLeft, Bot, Sparkles, Send, Package, Users, Truck, Swords, MessageSquareText, Globe, History, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTheme } from '../context/ThemeContext';
import { getAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebResearch = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  const [activeMode, setActiveMode] = useState(null);
  const resultsRef = useRef(null);

  // History state
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Objections state
  const [commonObjections, setCommonObjections] = useState([]);
  const [showObjections, setShowObjections] = useState(false);
  const [expandedObjection, setExpandedObjection] = useState(null);
  const [customObjection, setCustomObjection] = useState('');
  const [aiObjectionResponse, setAiObjectionResponse] = useState(null);
  const [isLoadingObjection, setIsLoadingObjection] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) navigate('/auth');
    else {
      fetchHistory();
      fetchObjections();
    }
  }, [navigate]);

  const fetchHistory = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/research/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (err) {
      console.error('Error fetching research history:', err);
    }
  };

  const fetchObjections = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/research/objections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommonObjections(res.data);
    } catch (err) {
      console.error('Error fetching objections:', err);
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/research/history/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(prev => prev.filter(h => h.id !== id));
      toast.success('History item removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleAiObjection = async () => {
    if (!customObjection.trim() || isLoadingObjection) return;
    setIsLoadingObjection(true);
    setAiObjectionResponse(null);
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/dealer/research/objection-ai`,
        { objection: customObjection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiObjectionResponse(res.data.response);
    } catch (err) {
      toast.error('Failed to generate response');
    } finally {
      setIsLoadingObjection(false);
    }
  };

  const handleSearch = async (query = null, mode = null) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim() || isSearching) return;

    setIsSearching(true);
    setResults(null);
    setLastQuery(searchTerm);
    setActiveMode(mode);

    let enhancedQuery = searchTerm;
    if (mode === 'tow') {
      enhancedQuery = `What RV types can a ${searchTerm} tow? Include towing capacity, compatible trailer weights, and recommendations.`;
    } else if (mode === 'compare') {
      enhancedQuery = `Sales comparison: ${searchTerm}. Include pros/cons of each, key differences, and which type of buyer each is best for.`;
    }

    try {
      const token = getAuthToken();
      const response = await axios.post(`${API}/research/search`,
        { query: enhancedQuery },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResults({
        query: searchTerm,
        summary: response.data.summary,
        google_search_url: response.data.google_search_url,
        mode: mode
      });
      setSearchQuery('');

      // Refresh history after new search
      fetchHistory();

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleFollowUp = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim()) return;
    const query = chatInput.trim();
    setChatInput('');
    handleSearch(query, activeMode);
  };

  const loadHistoryItem = (item) => {
    setResults({
      query: item.query,
      summary: item.summary,
      google_search_url: item.google_search_url,
      mode: item.mode,
    });
    setLastQuery(item.query);
    setActiveMode(item.mode);
    setShowHistory(false);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const formatSummary = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, idx) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <h3 key={idx} className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mt-4 mb-2 first:mt-0`}>
            {line.replace(/\*\*/g, '')}
          </h3>
        );
      }
      if (line.includes('**') && line.includes(':')) {
        const parts = line.split('**');
        return (
          <h3 key={idx} className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mt-4 mb-2 first:mt-0`}>
            {parts.filter(p => p.trim() && p !== ':').join('')}
          </h3>
        );
      }
      if (line.trim().startsWith('•')) {
        return (
          <div key={idx} className="flex items-start gap-2 ml-2 mb-1">
            <span className="text-[#F2A900] mt-1">•</span>
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{line.replace('•', '').trim()}</span>
          </div>
        );
      }
      if (line.trim()) {
        return <p key={idx} className={`${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{line}</p>;
      }
      return null;
    });
  };

  const quickActions = [
    {
      id: 'tow',
      icon: Truck,
      label: 'Tow Match',
      color: 'bg-blue-500 hover:bg-blue-600',
      placeholder: 'Enter vehicle (e.g., 2022 Ford F-150 EcoBoost)',
      description: 'Find out what RVs a vehicle can tow'
    },
    {
      id: 'compare',
      icon: Swords,
      label: 'Compare RVs',
      color: 'bg-purple-500 hover:bg-purple-600',
      placeholder: 'Enter comparison (e.g., Jayco vs Grand Design)',
      description: 'Get sales talking points for comparisons'
    },
  ];

  const [selectedAction, setSelectedAction] = useState(null);

  const formatHistoryDate = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} transition-colors duration-200`}>
      {/* Header */}
      <header className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dealer" className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
                <ArrowLeft size={20} />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#004E86] to-[#0070B8] rounded-lg flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>RV Sales Assistant</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center gap-1 ${showHistory ? 'text-[#004E86]' : ''}`}
                data-testid="toggle-history-btn"
              >
                <History size={16} />
                <span className="hidden sm:inline">History</span>
                {history.length > 0 && (
                  <span className="text-xs bg-[#004E86] text-white rounded-full w-5 h-5 flex items-center justify-center">{history.length}</span>
                )}
              </Button>
              <span className={`text-xs ${isDark ? 'bg-amber-900/50 text-amber-300 border-amber-700' : 'bg-amber-50 text-amber-700 border-amber-200'} px-3 py-1.5 rounded-full border flex items-center gap-1`}>
                <Sparkles size={14} />
                AI Powered
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Nav */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4 py-2 text-sm">
            <Link to="/dealer" className={`${isDark ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-[#004E86]'} flex items-center gap-1`}>
              <Users size={14} /> Leads
            </Link>
            <Link to="/dealer/inventory" className={`${isDark ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-[#004E86]'} flex items-center gap-1`}>
              <Package size={14} /> Inventory
            </Link>
            <span className={`${isDark ? 'text-blue-400' : 'text-[#004E86]'} font-medium flex items-center gap-1`}>
              <Bot size={14} /> Sales Assistant
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search History Panel */}
        {showHistory && (
          <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`} data-testid="history-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <History size={18} /> Search History
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)} className="text-gray-400">
                Close
              </Button>
            </div>
            {history.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} py-4 text-center`}>No searches yet. Your research history will appear here.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                    data-testid={`history-item-${item.id}`}
                  >
                    <div className="flex-1 min-w-0 mr-3" onClick={() => loadHistoryItem(item)}>
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {item.query}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatHistoryDate(item.created_at)}
                        {item.mode && ` • ${item.mode}`}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteHistoryItem(item.id); }}
                      className="text-gray-400 hover:text-red-500 p-1"
                      data-testid={`delete-history-${item.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            RV Sales Assistant
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Quick tools to help you close more deals
          </p>
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => setSelectedAction(selectedAction === action.id ? null : action.id)}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedAction === action.id
                  ? `${action.color} text-white border-transparent`
                  : isDark
                    ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
              data-testid={`action-${action.id}`}
            >
              <action.icon size={24} className="mx-auto mb-2" />
              <p className="font-semibold text-sm">{action.label}</p>
            </button>
          ))}
        </div>

        {/* Action-specific Input */}
        {selectedAction && (
          <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
            <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {quickActions.find(a => a.id === selectedAction)?.description}
            </p>
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(null, selectedAction)}
                placeholder={quickActions.find(a => a.id === selectedAction)?.placeholder}
                className={`flex-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
                disabled={isSearching}
                data-testid="action-input"
              />
              <Button
                onClick={() => handleSearch(null, selectedAction)}
                disabled={isSearching || !searchQuery.trim()}
                className={quickActions.find(a => a.id === selectedAction)?.color}
                data-testid="action-search-btn"
              >
                {isSearching ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
              </Button>
            </div>
          </div>
        )}

        {/* General Search */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm mb-6`}>
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Or search anything about RVs, camping, towing...
          </p>
          <div className="relative">
            <Input
              value={selectedAction ? '' : searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSelectedAction(null); }}
              onKeyDown={(e) => e.key === 'Enter' && !selectedAction && handleSearch()}
              placeholder="Ask anything..."
              className={`w-full pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
              disabled={isSearching || selectedAction}
              data-testid="general-search-input"
            />
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
            <Button
              onClick={() => handleSearch()}
              disabled={isSearching || !searchQuery.trim() || selectedAction}
              className="absolute right-1 top-1/2 -translate-y-1/2 bg-[#004E86] hover:bg-[#003d6b] h-8"
              data-testid="general-search-btn"
            >
              {isSearching ? <Loader2 className="animate-spin" size={16} /> : 'Search'}
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isSearching && (
          <div className={`text-center py-12 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
            <Loader2 className="animate-spin mx-auto text-[#004E86] mb-4" size={40} />
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              {activeMode === 'tow' && 'Calculating towing capacity...'}
              {activeMode === 'compare' && 'Building comparison...'}
              {!activeMode && `Researching "${lastQuery}"...`}
            </p>
          </div>
        )}

        {/* Results Section */}
        {results && !isSearching && (
          <div ref={resultsRef} className="space-y-4 mb-8">
            {/* Query Header with Mode Badge */}
            <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
              <div>
                {results.mode && (
                  <span className={`text-xs px-2 py-1 rounded-full mb-1 inline-block ${
                    results.mode === 'tow' ? 'bg-blue-100 text-blue-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {results.mode === 'tow' && 'Tow Match'}
                    {results.mode === 'compare' && 'Comparison'}
                  </span>
                )}
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>"{results.query}"</h2>
              </div>
            </div>

            {/* AI Summary */}
            <div className={`p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="text-[#F2A900]" size={20} />
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>AI Response</h3>
              </div>
              <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                {formatSummary(results.summary)}
              </div>
            </div>

            {/* Follow-up Search */}
            <div className={`p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
              <form onSubmit={handleFollowUp} className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask a follow-up question..."
                  className={`flex-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
                  data-testid="followup-input"
                />
                <Button
                  type="submit"
                  disabled={!chatInput.trim() || isSearching}
                  className="bg-[#004E86] hover:bg-[#003d6b]"
                  data-testid="followup-btn"
                >
                  <Send size={18} />
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Common Objections & Responses Section */}
        <div className={`p-6 rounded-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`} data-testid="objections-section">
          <button
            onClick={() => setShowObjections(!showObjections)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-objections-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquareText size={20} className="text-green-700" />
              </div>
              <div className="text-left">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Common Objections & Responses
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Ready-made scripts for common customer objections
                </p>
              </div>
            </div>
            {showObjections ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
          </button>

          {showObjections && (
            <div className="mt-6 space-y-4">
              {/* AI Custom Objection Input */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50 border border-gray-600' : 'bg-green-50 border border-green-200'}`}>
                <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Have a custom objection? Get an AI-powered response:
                </p>
                <div className="flex gap-2">
                  <Input
                    value={customObjection}
                    onChange={(e) => setCustomObjection(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAiObjection()}
                    placeholder='e.g., "We want to try tent camping first"'
                    className={`flex-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
                    disabled={isLoadingObjection}
                    data-testid="custom-objection-input"
                  />
                  <Button
                    onClick={handleAiObjection}
                    disabled={!customObjection.trim() || isLoadingObjection}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="ai-objection-btn"
                  >
                    {isLoadingObjection ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                  </Button>
                </div>
                {aiObjectionResponse && (
                  <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-green-200'}`} data-testid="ai-objection-response">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={14} className="text-[#F2A900]" />
                      <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>AI Response</span>
                    </div>
                    <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{aiObjectionResponse}</p>
                  </div>
                )}
              </div>

              {/* Static Objections List */}
              <div className="space-y-2">
                {commonObjections.map((obj, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg border transition-all ${
                      expandedObjection === idx
                        ? isDark ? 'border-green-600 bg-gray-700/50' : 'border-green-400 bg-green-50/50'
                        : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`objection-item-${idx}`}
                  >
                    <button
                      onClick={() => setExpandedObjection(expandedObjection === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          obj.category === 'Price' ? 'bg-red-100 text-red-700' :
                          obj.category === 'Stall' ? 'bg-yellow-100 text-yellow-700' :
                          obj.category === 'Value' ? 'bg-blue-100 text-blue-700' :
                          obj.category === 'Cost' ? 'bg-orange-100 text-orange-700' :
                          obj.category === 'Fear' ? 'bg-purple-100 text-purple-700' :
                          obj.category === 'Decision Maker' ? 'bg-pink-100 text-pink-700' :
                          obj.category === 'Usage' ? 'bg-teal-100 text-teal-700' :
                          obj.category === 'Competition' ? 'bg-indigo-100 text-indigo-700' :
                          obj.category === 'Logistics' ? 'bg-gray-100 text-gray-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {obj.category}
                        </span>
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          "{obj.objection}"
                        </span>
                      </div>
                      {expandedObjection === idx ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                    </button>
                    {expandedObjection === idx && (
                      <div className={`px-3 pb-3`}>
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>Suggested Response:</p>
                          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{obj.response}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WebResearch;
